import { Analytics } from '@cobliteam/analytics';
import type { RouteDef } from '@cobliteam/dashboard/src/dashboardDef';
import { useDashboard } from '@cobliteam/dashboard/src/providers/DashboardProvider';
import { useLanguage, useTranslation } from '@cobliteam/i18n';
import Input from '@cobliteam/ui/form/Input';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import { getTextForLanguage } from '@cobliteam/ui-business/utils';
import { Fragment, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './Collapsible';
import LinkWrapper from './LinkWrapper';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu as SidebarMenuBase,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from './Sidebar';

const mobileMenuItemClassName =
  'h-10 rounded-lg px-3 font-medium text-gray-3 text-sm transition-colors hover:bg-sidebar-mobile-accent/60 hover:text-white hover:[&>svg]:text-white data-[active=true]:bg-primary data-[active=true]:font-semibold data-[active=true]:text-white data-[active=true]:[&>svg]:text-white [&>svg]:size-5 [&>svg]:text-gray-4';

const mobileSectionLabelClassName = '!h-auto !text-gray-7 w-full px-3 pt-5 pb-1.5 font-semibold text-[0.6875rem] uppercase tracking-[0.08em]';

const mobileLinkClassName = 'rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-sidebar-mobile-ring focus-visible:ring-offset-0';

const normalizeSearch = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getRouteTitle = (route: RouteDef, language: string) => getTextForLanguage(route.sidebar?.title, language);

const routeMatchesSearch = (route: RouteDef, search: string, language: string) => normalizeSearch(getRouteTitle(route, language)).includes(search);

const getVisibleRoutes = (routes: RouteDef[] | undefined): RouteDef[] | undefined => {
  if (!routes) return undefined;

  return routes
    .filter((route) => !route.sidebar?.hide)
    .map((route) => ({
      ...route,
      routes: getVisibleRoutes(route.routes),
    }));
};

const filterRoutesBySearch = (routes: RouteDef[], search: string, language: string): RouteDef[] => {
  if (!search) return getVisibleRoutes(routes) ?? [];

  return routes.reduce<RouteDef[]>((filteredRoutes, route) => {
    if (route.sidebar?.hide) return filteredRoutes;

    const matchesRoute = routeMatchesSearch(route, search, language);
    const filteredChildren = route.routes ? filterRoutesBySearch(route.routes, search, language) : undefined;

    if (matchesRoute || (filteredChildren && filteredChildren.length > 0)) {
      filteredRoutes.push({
        ...route,
        routes: matchesRoute ? getVisibleRoutes(route.routes) : filteredChildren,
      });
    }

    return filteredRoutes;
  }, []);
};

export function SidebarMenuMobile() {
  const { sidebar } = useDashboard();
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation('ui-business');
  const { openMobile, setOpenMobile } = useSidebar();
  const [openGroups, setOpenGroups] = useState<{ [key: string]: boolean }>({});
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const menuListRef = useRef<HTMLDivElement>(null);

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Tab' || event.shiftKey) return;
    const firstFocusable = menuListRef.current?.querySelector<HTMLElement>('a[href], button:not([disabled])');
    if (!firstFocusable) return;
    event.preventDefault();
    firstFocusable.focus();
  };

  useEffect(() => {
    if (!openMobile) return;
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 100);
    return () => window.clearTimeout(id);
  }, [openMobile]);

  const router = sidebar.router;
  const normalizedSearch = normalizeSearch(search);
  const filteredRouter = useMemo(() => filterRoutesBySearch(router, normalizedSearch, currentLanguage), [router, normalizedSearch, currentLanguage]);

  const handleGroupToggle = (title: string, isOpen: boolean) => {
    Analytics.track('Sidebar - Group - Toggle', { title, isOpen });
    setOpenGroups((prev) => ({
      ...prev,
      [title]: isOpen,
    }));
  };

  const handleNavigate = (destination: string) => {
    setOpenMobile(false);
    Analytics.track('Sidebar - Navigate', { destination });
  };

  return (
    <div className='flex min-h-0 flex-col pb-6'>
      <div className='sticky top-0 z-10 border-sidebar-mobile-border border-b bg-sidebar-mobile px-3 pt-3 pb-3'>
        <Input
          ref={searchInputRef}
          data-testid='sidebar-mobile-search'
          name='sidebar-mobile-search'
          placeholder={t('app_sidebar.search_placeholder')}
          value={search}
          startIcon={<Icon name='search' className='size-5 text-gray-7' />}
          endIcon={
            search ? (
              <button
                type='button'
                aria-label={t('app_sidebar.clear_search')}
                className='flex size-6 items-center justify-center rounded-md text-gray-7 transition-colors hover:bg-gray-10 hover:text-gray-3'
                onClick={() => setSearch('')}
              >
                <Icon name='x' className='size-4' />
              </button>
            ) : null
          }
          className='!border-sidebar-mobile-border focus-visible:!border-sidebar-mobile-ring h-10 rounded-xl bg-gray-11 text-gray-2 text-sm placeholder:text-gray-7'
          size='sm'
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
      </div>
      <div ref={menuListRef}>
        {filteredRouter.length === 0 && (
          <div className='mx-3 mt-8 flex flex-col items-center gap-2 rounded-xl border border-sidebar-mobile-border bg-gray-11/60 px-4 py-6 text-center'>
            <Icon name='search' className='size-5 text-gray-7' />
            <p className='font-medium text-gray-3 text-sm'>{t('app_sidebar.empty_search_title')}</p>
            <p className='text-gray-7 text-xs'>{t('app_sidebar.empty_search_description')}</p>
          </div>
        )}
        {filteredRouter?.map((router, index) => {
          if (router.sidebar?.hide) return null;
          const routerTitle = getTextForLanguage(router.sidebar?.title, currentLanguage);
          const routerRoutes = router.routes?.filter((route) => !route.sidebar?.hide);

          // if the nav item has no visible children, show a simple menu item
          if (!routerRoutes?.length) {
            return (
              <SidebarGroup key={routerTitle} className={cn(index === 0 ? 'mt-2' : 'mt-0.5')}>
                <SidebarMenuBase className='px-3'>
                  <SidebarMenuItem>
                    <LinkWrapper to={router.redirect || router?.url || '#'} pageId={router.id} className={mobileLinkClassName} onClick={() => handleNavigate(routerTitle)}>
                      <SidebarMenuButton tooltip={routerTitle} size='sm' className={mobileMenuItemClassName} isActive={router.sidebar?.active}>
                        {router.sidebar?.icon && <Icon name={router.sidebar?.icon as IconNames} />}
                        <span>{routerTitle}</span>
                      </SidebarMenuButton>
                    </LinkWrapper>
                  </SidebarMenuItem>
                </SidebarMenuBase>
              </SidebarGroup>
            );
          }

          return (
            <SidebarGroup key={routerTitle} className={cn(index === 0 ? 'mt-1' : 'mt-2')}>
              <SidebarGroupLabel data-testid='sidebar-section-title' className={mobileSectionLabelClassName}>
                {routerTitle}
              </SidebarGroupLabel>
              {routerRoutes.map((route: RouteDef) => {
                if (route.sidebar?.hide) return null;
                const isActive = route.sidebar?.active;
                const routeTitle = getTextForLanguage(route.sidebar?.title, currentLanguage);
                return (
                  <Fragment key={routeTitle}>
                    {route?.routes?.some((item) => !item.sidebar?.hide) ? (
                      <SidebarGroup key={routeTitle}>
                        <Collapsible
                          key={route.id}
                          className='group/collapsible'
                          open={normalizedSearch ? true : openGroups[routeTitle || '']}
                          onOpenChange={(isOpen) => handleGroupToggle(routeTitle || '', isOpen)}
                        >
                          <CollapsibleTrigger className='group/trigger mx-3 flex h-10 w-[calc(100%-1.5rem)] items-center justify-between gap-2 rounded-lg px-3 font-medium text-gray-3 text-sm outline-none transition-colors hover:bg-sidebar-mobile-accent/60 hover:text-white focus-visible:ring-2 focus-visible:ring-sidebar-mobile-ring focus-visible:ring-offset-0'>
                            <span className='flex min-w-0 items-center gap-3'>
                              {route.sidebar?.icon && <Icon name={route.sidebar?.icon as IconNames} className='size-5 shrink-0 text-gray-4 group-hover/trigger:text-white' />}
                              <span className='truncate'>{routeTitle}</span>
                            </span>
                            <Icon
                              name='chevron-down'
                              className='ml-auto size-4 shrink-0 text-gray-6 transition-transform duration-200 group-hover/trigger:text-white group-data-[state=open]/collapsible:rotate-180'
                            />
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <SidebarGroupContent>
                              <SidebarMenuSub className='mx-6 my-1 gap-0.5 border-sidebar-mobile-border/60 py-1 pr-0 pl-4'>
                                {route?.routes?.map((item) => {
                                  if (item.sidebar?.hide) return null;
                                  const isActive = item.sidebar?.active;
                                  const goTo = item.redirect || item?.url || '#';
                                  const itemTitle = getTextForLanguage(item.sidebar?.title, currentLanguage);
                                  return (
                                    <SidebarMenuSubItem key={itemTitle}>
                                      <SidebarMenuSubButton
                                        asChild
                                        isActive={isActive}
                                        className='min-h-9 rounded-lg px-3 font-medium text-gray-4 text-sm hover:bg-sidebar-mobile-accent/60 hover:text-white data-[active=true]:bg-primary data-[active=true]:font-semibold data-[active=true]:text-white'
                                      >
                                        <LinkWrapper to={goTo} pageId={item.id} className={mobileLinkClassName} onClick={() => handleNavigate(itemTitle)}>
                                          {itemTitle}
                                        </LinkWrapper>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  );
                                })}
                              </SidebarMenuSub>
                            </SidebarGroupContent>
                          </CollapsibleContent>
                        </Collapsible>
                      </SidebarGroup>
                    ) : (
                      <SidebarGroup key={routeTitle}>
                        <SidebarMenuBase className='px-3'>
                          <SidebarMenuItem>
                            <LinkWrapper to={route.redirect || route?.url || '#'} pageId={route.id} className={mobileLinkClassName} onClick={() => handleNavigate(routeTitle)}>
                              <SidebarMenuButton tooltip={routeTitle} size='sm' className={mobileMenuItemClassName} isActive={isActive}>
                                {route.sidebar?.icon && <Icon name={route.sidebar?.icon as IconNames} />}
                                <span>{routeTitle}</span>
                              </SidebarMenuButton>
                            </LinkWrapper>
                          </SidebarMenuItem>
                        </SidebarMenuBase>
                      </SidebarGroup>
                    )}
                  </Fragment>
                );
              })}
            </SidebarGroup>
          );
        })}
      </div>
    </div>
  );
}
